import {
	Anchor,
	Form,
	FormItem,
	Tabs,
	TextInput,
	ToolbarAction,
	ValidationError
} from "client/Components";
import {
	EditUserGroupPageModel,
	getCreateViewModel,
	getEditViewModel,
	save,
	saveMemberPermissions,
	SaveUserGroupMemberPermissionsModel,
	UserGroupManagementMode,
	UserGroupModel,
	UsersAndUserGroupsDataModel
} from "client/Api/UserGroupsManagement/userGroupsManagement";
import {
	attachToUserAndUserGroupCollection
} from "client/Features/Users/UserAndUserGroupCollection/attachable";
import {
	UserAndUserGroupCollection,
	UserAndUserGroupKind
} from "client/Features/Users/UserAndUserGroupCollection/UserAndUserGroupCollection";
import {
	ActiveTab
} from "client/Features/Users/UserAndUserGroupTabSelector/UserAndUserGroupTabSelector";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	AlertManager,
	MediaQueryKind,
	useAlerts,
	useUnmountedCancellation,
	useWorkspaceMediaQuery
} from "client/Hooks";
import {
	ensureNotNil,
	isNotNil
} from "client/Misc/utils";
import { tryParseInt } from "client/Misc/parseUtils";
import { ActionKind, buildActionsForTopBar } from "client_legacy/ndaw/actions";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import { never } from "client_legacy/ndaw/errors";
import {
	FunctionComponent,
	ReactElement,
	useCallback,
	useEffect,
	useMemo,
	useState
} from "react";
import { SettingsPage } from "../SettingsPage";
import { useActiveSettingsMenuItem } from "../SidebarMenu/sidebarMenuSelectionStore";
import styles from "./EditUserGroupPage.module.less";
import classNames from "classnames";
import {
	createValidationConfiguration,
	InputValidationResult,
	InputValidationResultKind,
	IRootValidation
} from "client/Validation";
import { isNotEmpty, isNotNullNorUndefined } from "client/Validation/rules";
import { configureValidationStep, IObjectValidationStepBuilder } from "client/Validation/steps";
import { useUserGroupPermissionExplanationDialog } from "../UserGroupPermissions/UserGroupPermissionExplanationDialog";
import { UserGroupPermission, UserGroupPermissionsModel } from "client/Api/UserGroupsManagement/Permissions/userGroupPermissions";
import { userGroupManagementRouting } from "./UserGroupManagementPage";
import { handlePageDataLoadingError } from "client/Features/ErrorPage/errorStore";
import {
	NavigationCallback,
	PredefinedPropOptionKind,
	RouteParametersFromProps,
	connectToRouter
} from "client/Navigation";
import { SettingsPageKind } from "client/Api/SettingsLayout/settingsLayout";
import { UserGroupPermissionsSection } from "../UserGroupPermissions/UserGroupPermissionsSection";
import { IValueInputAttachable } from "client/Forms/attachable";
import { AiAssistantFabSpacing } from "client/Features/ChatAssistant/AiAssistantFabSpacing";

export type EditUserGroupPageProps = {
	userGroupId?: number;
	onNavigate: NavigationCallback;
};

export const EditUserGroupPage = (props: EditUserGroupPageProps): ReactElement => {
	const {
		userGroupId,
		onNavigate
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [viewModel, setViewModel] = useState<EditUserGroupPageModel>(getDefaultViewModel());
	const [userGroupName, setUserGroupName] = useState<string>("");
	const [formData, setFormData] = useState<FormData>(
		() => convertToFormData(createNewGroupModel())
	);
	const [activeTabId, setActiveTabId] = useState<TabId>("settings");

	const alertManager: AlertManager = useAlerts();
	const isResponsive: boolean = useWorkspaceMediaQuery(MediaQueryKind.Responsive);
	const globalCancellation: AbortSignal = useUnmountedCancellation();

	useActiveSettingsMenuItem(SettingsPageKind.UserGroupManagement);

	useEffect(
		(): EffectWithDestructorResult => {
			async function fetchData(signal: AbortSignal): Promise<void> {
				try {
					setIsBusy(true);
					let viewModel: EditUserGroupPageModel;

					if (isNotNil(userGroupId)) {
						viewModel = await getEditViewModel(userGroupId, signal)
							.catch(handlePageDataLoadingError);
					} else {
						viewModel = await getCreateViewModel(signal)
							.catch(handlePageDataLoadingError);
					}

					setViewModel(viewModel);

					if (viewModel.userGroupManagementMode === UserGroupManagementMode.ReadonlyExceptMemberPermissions) {
						setActiveTabId("permissions");
					}

					setFormData(convertToFormData(viewModel.userGroup ?? createNewGroupModel()));
					setUserGroupName(
						viewModel.userGroup
							?.name ?? ""
					);
					setIsBusy(false);
				} catch(e) {
					if (isCancelledError(e)) {
						return;
					}

					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
					setIsBusy(false);
				}
			}

			const cancellation = new AbortController();

			fetchData(cancellation.signal);

			return () => cancellation.abort();
		},
		[alertManager, userGroupId]
	);

	const {
		userGroupManagementMode: mode,
		usersAndUserGroupsData
	} = viewModel;

	const formHandler: IFormHandler<FormData> = useFormHandler(
		formData,
		setFormData,
		mode === UserGroupManagementMode.Edit
			? editValidation
			: permissionValidation
	);

	useEffect(
		(): void => {
			if (mode === UserGroupManagementMode.ReadonlyExceptMemberPermissions) {
				alertManager.showInfo(localize("_CanNotEditSomeDataBecauseADSync"));
			}
		},
		[alertManager, mode]
	);

	const navigateToOverviewHandler = useCallback(
		(): void => {
			onNavigate(userGroupManagementRouting.overview);
		},
		[onNavigate]
	);

	const saveHandler = useCallback(
		async (): Promise<void> => {
			if (isBusy) {
				return;
			}

			setIsBusy(true);

			const validation: IRootValidation<FormData> = formHandler.validation;

			try {
				const validationResult: InputValidationResult = await validation
					.validate(globalCancellation);

				if (validationResult.kind !== InputValidationResultKind.Valid) {
					setIsBusy(false);
					return;
				}

				const userGroupModel: UserGroupModel = convertToUserGroupModel(formData);

				switch (mode) {
					case UserGroupManagementMode.Edit:
						await save(userGroupModel, globalCancellation);
						break;
					case UserGroupManagementMode.ReadonlyExceptMemberPermissions:
						const model: SaveUserGroupMemberPermissionsModel = {
							id: ensureNotNil(userGroupModel.id),
							permissions: userGroupModel.permissions
						};

						await saveMemberPermissions(model, globalCancellation);
						break;
					default:
						never(mode);
				}

				navigateToOverviewHandler();
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				if (!validation.tryProcessServerError(e)) {
					alertManager.showError(localize("_ErrorOfUserGroupSaving"));
				}

				setIsBusy(false);
			}
		},
		[
			alertManager,
			formData,
			globalCancellation,
			isBusy,
			mode,
			formHandler.validation,
			navigateToOverviewHandler
		]
	);

	const defaultActions = useMemo(
		(): readonly ToolbarAction<undefined>[] => {
			return [
				{
					kind: ActionKind.Regular,
					name: "save",
					title: localize("_Save"),
					canExecute: () => true,
					execute: () => saveHandler(),
					icon: "floppy-disk",
					isPrimary: true
				},
				{
					kind: ActionKind.Regular,
					name: "cancel",
					title: localize("_Cancel"),
					canExecute: () => true,
					execute: () => navigateToOverviewHandler(),
					icon: "xmark"
				}
			];
		},
		[navigateToOverviewHandler, saveHandler]
	);

	const topBarActions = useMemo(
		(): readonly ToolbarAction<undefined>[] => {
			return buildActionsForTopBar<undefined, ToolbarAction<undefined>>(
				{
					defaultActions,
				},
				false,
				isResponsive,
				false
			);
		},
		[
			defaultActions,
			isResponsive
		]
	);

	return (
		<SettingsPage
			hasBusyIndicator={isBusy}
			onBack={navigateToOverviewHandler}
			actions={topBarActions}
			actionContext={undefined}
			title={
				isNotNil(userGroupId)
					? userGroupName
					: localize("_NewGroup")
			}
		>
			<SettingsPage.Section height="stretch">
				<Form onSubmit="NONE" height="fill">
					<Tabs<TabId>
						height="fill"
						activeTabId={activeTabId}
						onActiveTabIdChange={setActiveTabId}
					>
						<Tabs.Tab<TabId>
							{...formHandler.forStep(settingsValidationStep).attachToTab()}
							id="settings"
							title={localize("_Settings")}
						>
							<Settings
								formHandler={formHandler}
								mode={mode}
							/>
						</Tabs.Tab>

						<Tabs.Tab<TabId>
							id="permissions"
							title={localize("_Permissions")}
						>
							<Permissions
								attachable={formHandler.for("permissions")}
							/>
						</Tabs.Tab>

						<Tabs.Tab<TabId>
							id="members"
							title={localize("_Members")}
						>
							<Members
								formHandler={formHandler}
								mode={mode}
								usersAndUserGroupsData={usersAndUserGroupsData}
							/>
						</Tabs.Tab>
					</Tabs>
				</Form>
			</SettingsPage.Section>
			<AiAssistantFabSpacing />
		</SettingsPage>
	);
};
EditUserGroupPage.displayName = "EditUserGroupPage";

export type EditUserGroupPageParams = RouteParametersFromProps<
	EditUserGroupPageProps,
	"userGroupId"
>;

export const EditUserGroupPageForRouter: FunctionComponent = connectToRouter(
	EditUserGroupPage,
	{
		"userGroupId": {
			isRequired: false,
			tryParse: tryParseInt
		},
		"onNavigate": PredefinedPropOptionKind.NavigateCallback
	}
);

type TabId = "settings" | "permissions" | "members";

const permissionValidation = createValidationConfiguration<FormData>();

const editValidation = permissionValidation.clone();

editValidation.forScalar("name")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty()
	);

const settingsValidationStep: IObjectValidationStepBuilder<FormData> = configureValidationStep<FormData>()
	.add(
		"name",
		"description"
	);

type SettingsProps = {
	formHandler: IFormHandler<FormData>;
	mode: UserGroupManagementMode;
};

const Settings = (props: SettingsProps): ReactElement => {
	const {
		formHandler,
		mode
	} = props;

	return (
		<div
			className={classNames(
				styles.tabContent,
				styles.tabContent_widthContrained,
				"preserve-space-for-mobile-plus-button"
			)}
		>
			<FormItem label={localize("_Name")} isRequired>
				<TextInput
					{...formHandler.for("name").attachToTextInput()}
					isDisabled={mode === UserGroupManagementMode.ReadonlyExceptMemberPermissions}
				/>

				<ValidationError {...formHandler.for("name").attachToValidationError()} />
			</FormItem>

			<FormItem label={localize("_Description")}>
				<TextInput
					{...formHandler.for("description").attachToTextInput()}
					isDisabled={mode === UserGroupManagementMode.ReadonlyExceptMemberPermissions}
				/>

				<ValidationError {...formHandler.for("description").attachToValidationError()} />
			</FormItem>
		</div>
	);
};
Settings.displayName = "Settings";

type PermissionsProps = {
	attachable: IValueInputAttachable<UserGroupPermissionsModel>;
};

const Permissions = (props: PermissionsProps): ReactElement => {
	const {
		attachable
	} = props;

	const [
		userGroupPermissionExplanationDialog,
		openUserGroupPermissionExplanationDialog
	] = useUserGroupPermissionExplanationDialog();

	return (
		<div className={classNames(styles.tabContent, "preserve-space-for-mobile-plus-button")}>
			<Anchor
				className={styles.moreInfoAnchor}
				onClick={openUserGroupPermissionExplanationDialog}
			>
				{localize("_LearnMoreAboutPermissions")}
			</Anchor>

			<UserGroupPermissionsSection
				attachable={attachable}
			/>

			{userGroupPermissionExplanationDialog}
		</div>
	);
};
Settings.displayName = "Settings";

type MembersProps = {
	formHandler: IFormHandler<FormData>;
	mode: UserGroupManagementMode;
	usersAndUserGroupsData: UsersAndUserGroupsDataModel;
};

const Members = (props: MembersProps): ReactElement => {
	const {
		formHandler,
		mode,
		usersAndUserGroupsData
	} = props;

	return (
		<div className={styles.tabContent}>
			<UserAndUserGroupCollection
				activeTab={ActiveTab.User}
				{...attachToUserAndUserGroupCollection(
					formHandler.for("members"),
					{
						kind: mode === UserGroupManagementMode.Edit
							? UserAndUserGroupKind.Edit
							: UserAndUserGroupKind.Read,
						dialogTitleWhenAddingUsersAndUserGroups: localize("_AddMembers")
					}
				)}
				allUsersAndUserGroupsData={usersAndUserGroupsData}
			/>
		</div>
	);
};
Members.displayName = "Members";

type FormData = {
	id: number | null;
	name: string;
	description: string;
	members: {
		userIds: readonly number[];
		userGroupIds: readonly number[];
	};
	permissions: UserGroupPermissionsModel;
};

function convertToFormData(model: UserGroupModel): FormData {
	const {
		selectedUserIds,
		selectedUserGroupIds,
		...rest
	} = model;

	return {
		...rest,
		members: {
			userIds: selectedUserIds,
			userGroupIds: selectedUserGroupIds
		}
	};
}

function convertToUserGroupModel(model: FormData): UserGroupModel {
	const {
		members,
		...rest
	} = model;

	return {
		...rest,
		selectedUserIds: members.userIds,
		selectedUserGroupIds: members.userGroupIds
	};
}

function createNewGroupModel(): UserGroupModel {
	return {
		id: null,
		name: "",
		description: "",
		selectedUserIds: [],
		selectedUserGroupIds: [],
		permissions: {
			canCreateApps: UserGroupPermission.NotSet,
			canCreateSharedGroups: UserGroupPermission.NotSet,
			canCreateAnnouncementsAndCategories: UserGroupPermission.NotSet,
			canEditOwnWorkspace: UserGroupPermission.NotSet,
			canMakeUserRequests: UserGroupPermission.NotSet,
			canCreateKnowledgeItemsAndCategories: UserGroupPermission.NotSet,
			canCreateEventsAndCategories: UserGroupPermission.NotSet,
			canCreateHubTemplates: UserGroupPermission.NotSet,
			canUploadAndManageAppIcons: UserGroupPermission.NotSet,
			canCreateAndManageSpaces: UserGroupPermission.NotSet,
			canManageAllApps: UserGroupPermission.NotSet,
			canManageBillingModulesAndLicenses: UserGroupPermission.NotSet,
			canManageSharedTileGroups: UserGroupPermission.NotSet,
			canManageUsersGroupsAndUserProvisioning: UserGroupPermission.NotSet,
			canSendDirectPopups: UserGroupPermission.NotSet,
			canCreateAndManageCommunitiesAndCommunityPosts: UserGroupPermission.NotSet,
			canManageBranding: UserGroupPermission.NotSet,
			canCreateAndManageIntegrationBuilderIntegrations: UserGroupPermission.NotSet,
			canViewAnalytics: UserGroupPermission.NotSet
		}
	};
}

function getDefaultViewModel(): EditUserGroupPageModel {
	return {
		userGroup: null,
		userGroupManagementMode: UserGroupManagementMode.Edit,
		usersAndUserGroupsData: {
			users: [],
			userGroups: []
		}
	};
};